import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { CalendarRange } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function monthOptions() {
  const now = new Date();
  return Array.from({ length: 24 }, (_, i) => {
    const s = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const e = new Date(s.getFullYear(), s.getMonth() + 1, 0);
    return { value: iso(s), label: s.toLocaleString("en-US", { month: "long", year: "numeric" }), start: iso(s), end: iso(e) };
  });
}

export const RevenuePeriod = () => {
  const months = useMemo(monthOptions, []);
  const [mode, setMode] = useState(months[0].value);
  const [custom, setCustom] = useState({ start: months[2].start, end: iso(new Date()) });
  const range = mode === "custom" ? custom : months.find((m) => m.value === mode);
  const valid = range.start && range.end && range.start <= range.end;
  const { data, isFetching } = useQuery({
    queryKey: ["proc-revenue-period", range.start, range.end],
    queryFn: async () => (await api.get("/procedures/revenue", { params: { start: range.start, end: range.end } })).data,
    enabled: !!valid,
  });
  const p = data?.period;
  const rate = p && p.billed > 0 ? Math.round((p.collected / p.billed) * 100) : 0;

  return (
    <div data-testid="revenue-period-card" className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><CalendarRange className="h-4 w-4 text-cyan-600" /> Revenue by Period</h3>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <select data-testid="revenue-period-select" value={mode} onChange={(e) => setMode(e.target.value)}
            className="border border-slate-200 rounded-md px-3 text-sm h-9 bg-white">
            {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            <option value="custom">Custom range…</option>
          </select>
          {mode === "custom" && (
            <>
              <Input data-testid="revenue-period-start" type="date" value={custom.start} max={custom.end} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="h-9 w-40" />
              <span className="text-slate-400 text-sm">to</span>
              <Input data-testid="revenue-period-end" type="date" value={custom.end} min={custom.start} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="h-9 w-40" />
            </>
          )}
        </div>
      </div>
      {!valid ? <p data-testid="revenue-period-invalid" className="text-sm text-red-600">Start date must be on or before the end date.</p> : (
        <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 transition-opacity ${isFetching ? "opacity-50" : ""}`}>
          <Metric label="Billed" value={fmtCurrency(p?.billed || 0)} testid="revenue-period-billed" />
          <Metric label="Collected" value={fmtCurrency(p?.collected || 0)} sub={`${rate}% of billed`} testid="revenue-period-collected" cls="text-emerald-600" />
          <Metric label="Procedures" value={(p?.count || 0).toLocaleString()} sub={p ? `${p.start} → ${p.end}` : ""} testid="revenue-period-count" />
          <div data-testid="revenue-period-top" className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Top procedures</div>
            {(p?.by_procedure || []).slice(0, 3).map((t) => (
              <div key={t.name} className="flex justify-between gap-2 text-xs"><span className="truncate text-slate-700">{t.name}</span><span className="font-mono-metric text-slate-900">{fmtCurrency(t.revenue)}</span></div>
            ))}
            {p && p.by_procedure.length === 0 && <div className="text-xs text-slate-400">No billing in this period</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const Metric = ({ label, value, sub, testid, cls = "text-slate-900" }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div data-testid={testid} className={`font-mono-metric text-2xl font-bold ${cls}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);
